import { ChartData } from '@crm/api'
import { getColor } from '@crm/components/Charts/utils'
import { useStyles } from '@crm/features/reports/styles'
import { ChartDataItem } from '@crm/features/reports/types'
import { abbreviateNumber } from '@crm/utils'
import { useLocales } from '@shared/locales'
import { Card, Col, Empty, Flex, List, Row, Typography } from 'antd'
import { useCallback, useState } from 'react'
import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Sector,
  Tooltip
} from 'recharts'
import { PieSectorDataItem } from 'recharts/types/polar/Pie'

const { Title } = Typography

type Props = {
  label?: string
  subLabel?: string
  isLoading?: boolean
  data?: ChartData
}

export const PieChart = ({ label, subLabel, isLoading, data }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const [activeIndex, setActiveIndex] = useState<number | undefined>()

  const renderActiveShape = useCallback(
    (props: PieSectorDataItem) => {
      const { cx, cy, innerRadius, outerRadius, startAngle, endAngle } = props

      return (
        <g>
          <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={getColor(8, 'primary')}
          />
        </g>
      )
    },
    [t]
  )

  const dataMapping = data?.data as ChartDataItem[]

  return (
    <Card
      title={label}
      loading={isLoading}
      style={{
        minHeight: 360
      }}
    >
      {data ? (
        <Row>
          <Col span={12}>
            <Flex
              justify="center"
              align="center"
              vertical
              className={styles.chartTitle}
            >
              <Title level={3}>
                {data?.total && abbreviateNumber(data?.total)}
              </Title>
              {subLabel || data?.mapping[0]?.label}
            </Flex>

            <ResponsiveContainer width="100%" height="100%" minHeight={360}>
              <RechartsPieChart>
                <Tooltip
                  content={({ active, payload }) =>
                    active && payload ? (
                      <Flex vertical gap={4}>
                        {payload[0].payload.name}

                        {data?.mapping?.map((item, index) => (
                          <p key={index}>
                            {item.label}:{' '}
                            {payload[0].payload[item?.field_name || '']}
                          </p>
                        ))}
                      </Flex>
                    ) : null
                  }
                />

                {data?.mapping?.map((item, index) => (
                  <Pie
                    key={index}
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    data={dataMapping}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={(index + 1) * 120 - 10}
                    strokeWidth={0}
                    dataKey={item?.field_name || ''}
                    onMouseEnter={(_, index) => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(undefined)}
                  >
                    {dataMapping?.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={getColor(index, 'primary')}
                      />
                    ))}
                  </Pie>
                ))}
              </RechartsPieChart>
            </ResponsiveContainer>
          </Col>

          <Col span={12}>
            <List
              dataSource={dataMapping?.toReversed()}
              renderItem={(chartItem, key) => (
                <List.Item>
                  <List.Item.Meta
                    title={chartItem.name}
                    avatar={
                      <div
                        className={styles.listBullet}
                        style={{ background: getColor(key, 'primary') }}
                      />
                    }
                  />
                  {chartItem.Name_count}
                </List.Item>
              )}
            />
          </Col>
        </Row>
      ) : (
        <Empty />
      )}
    </Card>
  )
}
